// Core API
import api, { type AxiosResponse } from './client';
import type {
  Company,
  Site,
  Warehouse,
  Location,
  CompanyGroup,
  CompanyGroupMembersResponse,
  HierarchyTreeResponse,
  Role,
  UserProfile,
  UserManagementLog,
  UserSitePermission,
  SupplierCustomerRelationship,
  DashboardStats,
  CompanyRegistrationData,
  ViewContext,
  ViewContextUpdate,
  QueryParams,
  ActionResponse,
  LegalContent,
  LegalAcceptRequest,
  LegalAcceptResponse,
} from './types';

export const coreAPI = {
  // Dashboard
  // CR-3396: Accept view context params for hierarchical filtering
  getDashboardStats: (params?: Record<string, string | number>): Promise<AxiosResponse<DashboardStats>> =>
    api.get('/core/dashboard/', { params }),

  // Companies
  getCompanies: (params?: QueryParams): Promise<AxiosResponse<{ results: Company[] }>> =>
    api.get('/core/companies/', { params }),

  getCompany: (id: number): Promise<AxiosResponse<Company>> =>
    api.get(`/core/companies/${id}/`),

  // CR-3396: Get companies accessible to the current user (own company + group siblings).
  // CR-4887: pass { context: 'customer_filter' } from the Dashboard / Supplier Performance
  // filter panels so supplier-portal users get the CUSTOMER hierarchy they serve instead
  // of their own group siblings.
  getAccessibleCompanies: (params?: { context?: string }): Promise<AxiosResponse<{ results: Company[] }>> =>
    api.get('/core/companies/accessible/', { params }),

  // CR-4669: Companies in the current user's group — read-only index used by
  // the Supplier Profile's Group Structure tab. The main /core/companies/
  // list is gated by IsHelixUser, so supplier users get 403; this action is
  // open to any authenticated user but scoped server-side to their own group.
  getMyGroupMembers: (params?: { company_type?: string; group?: number }): Promise<AxiosResponse<{ count: number; results: Company[] }>> =>
    api.get('/core/companies/my_group_members/', { params }),

  // Supplier-Site options for the Dashboard filter dropdown. Portal-aware:
  // customer portal returns suppliers via SupplierCustomerRelationship,
  // supplier portal returns the user's own group siblings (their supplier
  // sites), helix returns all active suppliers.
  getSupplierSitesForFilter: (): Promise<AxiosResponse<{ results: Company[] }>> =>
    api.get('/core/companies/for-filter/'),

  createCompany: (data: Partial<Company>): Promise<AxiosResponse<Company>> =>
    api.post('/core/companies/', data),

  updateCompany: (id: number, data: Partial<Company>): Promise<AxiosResponse<Company>> =>
    api.put(`/core/companies/${id}/`, data),

  patchCompany: (id: number, data: Partial<Company>): Promise<AxiosResponse<Company>> =>
    api.patch(`/core/companies/${id}/`, data),

  deleteCompany: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/core/companies/${id}/`),

  uploadCompanyLogo: (id: number, file: File): Promise<AxiosResponse<Company>> => {
    const form = new FormData();
    form.append('logo', file);
    return api.post(`/core/companies/${id}/upload-logo/`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  getCompanySites: (id: number): Promise<AxiosResponse<Site[]>> =>
    api.get(`/core/companies/${id}/sites/`),

  getCompanyUsers: (id: number): Promise<AxiosResponse<UserProfile[]>> =>
    api.get(`/core/companies/${id}/users/`),

  approveCompanyUsers: (id: number, userIds: number[]): Promise<AxiosResponse<ActionResponse>> =>
    api.post(`/core/companies/${id}/approve_users/`, { user_ids: userIds }),

  // Sites
  getSites: (params?: QueryParams): Promise<AxiosResponse<{ results: Site[] }>> =>
    api.get('/core/sites/', { params }),

  // CR-3396: Get sites accessible to the current user.
  // CR-4887: pass { context: 'customer_filter' } from the Dashboard / Supplier Performance
  // filter panels so supplier-portal users get the CUSTOMER sites they serve instead
  // of their own authorised sites (the user-menu site switcher must NOT pass it).
  getAccessibleSites: (params?: { context?: string }): Promise<AxiosResponse<Site[]>> =>
    api.get('/core/sites/accessible/', { params }),

  getSite: (id: number): Promise<AxiosResponse<Site>> =>
    api.get(`/core/sites/${id}/`),

  createSite: (data: Partial<Site>): Promise<AxiosResponse<Site>> =>
    api.post('/core/sites/', data),

  updateSite: (id: number, data: Partial<Site>): Promise<AxiosResponse<Site>> =>
    api.put(`/core/sites/${id}/`, data),

  deleteSite: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/core/sites/${id}/`),

  // Warehouses
  getWarehouses: (params?: QueryParams): Promise<AxiosResponse<{ results: Warehouse[] }>> =>
    api.get('/core/warehouses/', { params }),

  // CR-4339: Get warehouses accessible to the current user
  getAccessibleWarehouses: (): Promise<AxiosResponse<Warehouse[]>> =>
    api.get('/core/warehouses/accessible/'),

  getWarehouse: (id: number): Promise<AxiosResponse<Warehouse>> =>
    api.get(`/core/warehouses/${id}/`),

  createWarehouse: (data: Partial<Warehouse>): Promise<AxiosResponse<Warehouse>> =>
    api.post('/core/warehouses/', data),

  updateWarehouse: (id: number, data: Partial<Warehouse>): Promise<AxiosResponse<Warehouse>> =>
    api.put(`/core/warehouses/${id}/`, data),

  deleteWarehouse: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/core/warehouses/${id}/`),

  // Locations
  getLocations: (params?: QueryParams): Promise<AxiosResponse<{ results: Location[] }>> =>
    api.get('/core/locations/', { params }),

  getLocation: (id: number): Promise<AxiosResponse<Location>> =>
    api.get(`/core/locations/${id}/`),

  createLocation: (data: Partial<Location>): Promise<AxiosResponse<Location>> =>
    api.post('/core/locations/', data),

  updateLocation: (id: number, data: Partial<Location>): Promise<AxiosResponse<Location>> =>
    api.put(`/core/locations/${id}/`, data),

  deleteLocation: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/core/locations/${id}/`),

  // Company Groups
  getCompanyGroups: (params?: QueryParams): Promise<AxiosResponse<{ results: CompanyGroup[] }>> =>
    api.get('/core/company-groups/', { params }),

  getCompanyGroup: (id: number): Promise<AxiosResponse<CompanyGroup>> =>
    api.get(`/core/company-groups/${id}/`),

  // CR-3396: Get the company group accessible to the current user
  getAccessibleCompanyGroup: (): Promise<AxiosResponse<CompanyGroup | null>> =>
    api.get('/core/company-groups/accessible/'),

  // Supplier-Group options for the Dashboard / Supplier-Performance filter
  // dropdown. Pass { context: 'customer_filter' } for supplier-portal users
  // to receive customer-side groups (mirrors CR-4887 on companies/sites).
  getSupplierGroupsForFilter: (
    params?: { context?: 'customer_filter' }
  ): Promise<AxiosResponse<{ results: CompanyGroup[] }>> =>
    api.get('/core/company-groups/for-filter/', { params }),

  createCompanyGroup: (data: Partial<CompanyGroup>): Promise<AxiosResponse<CompanyGroup>> =>
    api.post('/core/company-groups/', data),

  updateCompanyGroup: (id: number, data: Partial<CompanyGroup>): Promise<AxiosResponse<CompanyGroup>> =>
    api.put(`/core/company-groups/${id}/`, data),

  deleteCompanyGroup: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/core/company-groups/${id}/`),

  updateCompanyGroupPreferences: (id: number, data: Record<string, string | null>): Promise<AxiosResponse<Record<string, string | null>>> =>
    api.patch(`/core/company-groups/${id}/preferences/`, data),

  // CR-4579: Get member companies and aggregate stats for a group
  getCompanyGroupMembers: (id: number): Promise<AxiosResponse<CompanyGroupMembersResponse>> =>
    api.get(`/core/company-groups/${id}/members/`),

  // CR-5311: Get users across all member companies of a group
  getCompanyGroupUsers: (id: number): Promise<AxiosResponse<{ count: number; results: UserProfile[] }>> =>
    api.get(`/core/company-groups/${id}/users/`),

  // Hierarchy Tree
  getHierarchyTree: (params?: { include_inactive?: boolean; company_type?: 'customer' | 'supplier' }): Promise<AxiosResponse<HierarchyTreeResponse>> =>
    api.get('/core/hierarchy/', { params }),

  // Roles
  getRoles: (params?: QueryParams): Promise<AxiosResponse<{ results: Role[]; count: number }>> =>
    api.get('/core/roles/', { params }),

  getRole: (id: number): Promise<AxiosResponse<Role>> =>
    api.get(`/core/roles/${id}/`),

  // User Profiles
  getUserProfiles: (params?: QueryParams): Promise<AxiosResponse<{ results: UserProfile[] }>> =>
    api.get('/core/user-profiles/', { params }),

  getUserProfile: (id: number): Promise<AxiosResponse<UserProfile>> =>
    api.get(`/core/user-profiles/${id}/`),

  getMyProfile: (): Promise<AxiosResponse<UserProfile>> =>
    api.get('/core/user-profiles/me/'),

  approveUser: (id: number): Promise<AxiosResponse<ActionResponse>> =>
    api.post(`/core/user-profiles/${id}/approve/`),

  rejectUser: (id: number): Promise<AxiosResponse<ActionResponse>> =>
    api.post(`/core/user-profiles/${id}/reject/`),

  // Utility endpoints
  getPendingApprovals: (): Promise<AxiosResponse<{ count: number; users: UserProfile[] }>> =>
    api.get('/core/pending-approvals/'),

  getRecentRegistrations: (days?: number): Promise<AxiosResponse<{ count: number; companies: Company[] }>> =>
    api.get('/core/recent-registrations/', { params: { days } }),

  bulkApproveUsers: (userIds: number[]): Promise<AxiosResponse<ActionResponse>> =>
    api.post('/core/bulk-approve-users/', { user_ids: userIds }),

  // User Management Actions (for admins with can_manage_users permission)
  toggleUserActive: (id: number): Promise<AxiosResponse<{ status: string; is_active: boolean; message: string }>> =>
    api.post(`/core/user-profiles/${id}/toggle_active/`),

  // CR-5312: lifecycle delete. Backend hard-deletes when there's no
  // login / activity / management history; otherwise it deactivates the
  // user so the audit trail is preserved. Response carries `deleted` and
  // `deactivated` so the caller can render the right toast.
  safeDeleteUser: (id: number): Promise<AxiosResponse<{
    deleted: boolean;
    deactivated: boolean;
    message: string;
    is_active?: boolean;
    user?: UserProfile;
  }>> =>
    api.post(`/core/user-profiles/${id}/safe_delete/`),

  changeUserRole: (id: number, roleId: number): Promise<AxiosResponse<{ message: string; user: UserProfile }>> =>
    api.post(`/core/user-profiles/${id}/change_role/`, { role_id: roleId }),

  toggleUserAdmin: (id: number, isAdmin: boolean): Promise<AxiosResponse<{ message: string }>> =>
    api.post(`/core/user-profiles/${id}/toggle_admin/`, { is_admin: isAdmin }),

  sendInvitationEmail: (
    profileId: number
  ): Promise<AxiosResponse<{ message: string; user?: UserProfile }>> =>
    api.post(`/core/user-profiles/${profileId}/send_invitation/`),

  getUserManagementLogs: (id: number): Promise<AxiosResponse<UserManagementLog[]>> =>
    api.get(`/core/user-profiles/${id}/management_logs/`),

  getAvailableRoles: (): Promise<AxiosResponse<{ roles: Role[] }>> =>
    api.get('/core/user-profiles/available_roles/'),

  // Site Permissions
  // CR-5344: Optional companyId scopes the returned site list to that
  // company's group instead of the user's home company group. Use this
  // when opening the popup from a specific supplier's context to avoid
  // leaking the user's other-tenant site memberships.
  getUserSitePermissions: (
    userId: number,
    companyId?: number
  ): Promise<AxiosResponse<UserSitePermission[]>> =>
    api.get(`/core/user-profiles/${userId}/site_permissions/`, {
      params: companyId ? { scope_company: companyId } : undefined,
    }),

  updateUserSitePermission: (
    userId: number,
    siteId: number,
    data: Partial<UserSitePermission>
  ): Promise<AxiosResponse<UserSitePermission>> =>
    api.post(`/core/user-profiles/${userId}/site_permissions/`, { site_id: siteId, ...data }),

  deleteUserSitePermission: (userId: number, siteId: number): Promise<AxiosResponse<{ message: string }>> =>
    api.delete(`/core/user-profiles/${userId}/site_permissions/${siteId}/`),

  // Registration
  registerCompany: (data: CompanyRegistrationData): Promise<AxiosResponse<{ message: string; company: Company }>> =>
    api.post('/core/register/company/', data),

  getRegistrationInfo: (): Promise<AxiosResponse<{
    company_types: Array<{ value: string; label: string }>;
    site_types: Array<{ value: string; label: string }>;
    warehouse_types: Array<{ value: string; label: string }>;
  }>> =>
    api.get('/core/registration-info/'),

  getCompanyTypes: (): Promise<AxiosResponse<Array<{ value: string; label: string }>>> =>
    api.get('/core/company-types/'),

  // View Context - Database-backed view switching (replaces localStorage)
  getViewContext: (): Promise<AxiosResponse<ViewContext>> =>
    api.get('/core/view-context/'),

  updateViewContext: (data: Partial<ViewContextUpdate>): Promise<AxiosResponse<ViewContext>> =>
    api.patch('/core/view-context/', data),

  // Exchange Rates - Currency conversion
  getLatestExchangeRates: (): Promise<AxiosResponse<{
    base_currency: string;
    rates: Record<string, string>;
    as_of: string;
  }>> =>
    api.get('/core/exchange-rates/latest/'),

  getExchangeRatesForDate: (date: string): Promise<AxiosResponse<{
    base_currency: string;
    requested_date: string;
    rates: Record<string, string>;
  }>> =>
    api.get('/core/exchange-rates/for_date/', { params: { date } }),

  convertCurrency: (data: {
    amount: number;
    from_currency: string;
    to_currency: string;
    date?: string;
  }): Promise<AxiosResponse<{
    amount: string;
    from_currency: string;
    to_currency: string;
    converted_amount: string;
    exchange_rate: string;
    date: string;
  }>> =>
    api.post('/core/exchange-rates/convert/', data),

  // Version & Changelog
  getVersion: (): Promise<AxiosResponse<{
    version: string;
    build_date: string;
    build_number: number;
    git_commit: string;
    release_notes: string;
    server_time: string;
  }>> =>
    api.get('/core/version/'),

  getChangelog: (): Promise<AxiosResponse<{ changelog: string }>> =>
    api.get('/core/changelog/'),

  getAppInfo: (): Promise<AxiosResponse<{
    version: string;
    build_date: string;
    build_number: number;
    git_commit: string;
    release_notes: string;
    server_time: string;
    changelog: string;
  }>> =>
    api.get('/core/app-info/'),

  // Legal Content
  getLegalContent: (): Promise<AxiosResponse<LegalContent>> =>
    api.get('/core/legal/'),

  acceptLegal: (data: LegalAcceptRequest): Promise<AxiosResponse<LegalAcceptResponse>> =>
    api.post('/core/legal/accept/', data),

  // Supplier-Customer Relationships
  getSupplierRelationships: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: SupplierCustomerRelationship[] }>> =>
    api.get('/core/supplier-relationships/', { params }),

  updateSupplierRelationship: (id: number, data: Partial<SupplierCustomerRelationship>): Promise<AxiosResponse<SupplierCustomerRelationship>> =>
    api.patch(`/core/supplier-relationships/${id}/`, data),

  createSupplierRelationship: (data: { supplier: number; customer: number }): Promise<AxiosResponse<SupplierCustomerRelationship>> =>
    api.post('/core/supplier-relationships/', data),

  deleteSupplierRelationship: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/core/supplier-relationships/${id}/`),
};
